import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { motion, AnimatePresence } from 'framer-motion';
import { dbSelect, dbDelete, syncLocalStorageToSupabase } from '../../lib/supabase';
import Icon from '../../components/AppIcon';
import { useNavigate, useParams, Link } from 'react-router-dom';
import BlogAdminPanel from './BlogAdminPanel';


const ADMIN_PASS = import.meta.env.VITE_ADMIN_PASSWORD || 'bytepairs@2026';

const TABS = [
  { id: 'submissions',       label: 'Contact Requests',    icon: 'Mail',       color: 'blue'   },
  { id: 'project_inquiries', label: 'Project Estimations', icon: 'Calculator', color: 'purple' },
  { id: 'consultations',     label: 'Consultation Leads',  icon: 'Calendar',   color: 'emerald'},
];

const SOURCE_LABELS = {
  contact_form:   { label: 'Contact Form',   color: 'bg-blue-500/20 text-blue-400'    },
  homepage_cta:   { label: 'Homepage CTA',   color: 'bg-purple-500/20 text-purple-400'},
  global_modal:   { label: 'Quick Modal',    color: 'bg-emerald-500/20 text-emerald-400'},
};

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  try {
    return new Date(dateStr).toLocaleString('en-IN', {
      day: 'numeric', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return dateStr;
  }
};

const StatCard = ({ label, count, icon, color }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    className="bg-slate-900/60 border border-white/8 rounded-2xl p-6 flex items-center justify-between"
  >
    <div>
      <p className="text-slate-500 text-xs font-semibold uppercase tracking-wider mb-1">{label}</p>
      <h2 className="text-4xl font-bold text-white">{count}</h2>
    </div>
    <div className={`p-4 bg-${color}-500/10 rounded-xl`}>
      <Icon name={icon} size={26} className={`text-${color}-400`} />
    </div>
  </motion.div>
);

const EmptyState = ({ tab }) => (
  <tr>
    <td colSpan={5} className="px-8 py-24 text-center">
      <div className="flex flex-col items-center gap-4 text-slate-600">
        <Icon name="Inbox" size={48} />
        <p className="text-slate-500 font-medium">No {tab.replace('_', ' ')} yet</p>
        <p className="text-sm text-slate-600">Entries will appear here once clients submit forms.</p>
      </div>
    </td>
  </tr>
);

const AdminPage = ({ tab }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [isLoggedIn, setIsLoggedIn]   = useState(false);
  const [password, setPassword]       = useState('');
  const [loginError, setLoginError]   = useState('');
  const [activeTab, setActiveTab]     = useState('submissions');
  const [loading, setLoading]         = useState(false);
  const [deleting, setDeleting]       = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [data, setData]               = useState({
    submissions: [],
    project_inquiries: [],
    consultations: [],
  });

  useEffect(() => {
    if (localStorage.getItem('bp_admin_auth') === 'true') {
      setIsLoggedIn(true);
      syncLocalStorageToSupabase().finally(() => {
        fetchAll();
      });
    }
  }, []);

  const handleLogin = (e) => {
    e.preventDefault();
    if (password === ADMIN_PASS) {
      setIsLoggedIn(true);
      localStorage.setItem('bp_admin_auth', 'true');
      syncLocalStorageToSupabase().finally(() => {
        fetchAll();
      });
    } else {
      setLoginError('Incorrect password. Please try again.');
      setTimeout(() => setLoginError(''), 3000);
    }
  };

  const handleLogout = () => {
    setIsLoggedIn(false);
    localStorage.removeItem('bp_admin_auth');
    setData({ submissions: [], project_inquiries: [], consultations: [] });
  };

  const fetchAll = async () => {
    setLoading(true);
    try {
      const results = {};
      for (const tab of TABS) {
        const { data: rows } = await dbSelect(tab.id, 'createdAt', false);
        results[tab.id] = rows || [];
      }
      setData(results);
    } catch (err) {
      console.error('Fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Permanently delete this entry?')) return;
    setDeleting(id);
    try {
      await dbDelete(activeTab, id);
      setData(prev => ({
        ...prev,
        [activeTab]: prev[activeTab].filter(r => r.id !== id),
      }));
    } catch (err) {
      console.error('Delete error:', err);
    } finally {
      setDeleting(null);
    }
  };

  // ── Login screen ─────────────────────────────────────────────────────────────
  if (!isLoggedIn) {
    return (
      <>
        <Helmet>
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>
        <div className="min-h-screen bg-slate-950 flex items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md"
        >
          {/* Logo / Brand */}
          <div className="text-center mb-10">
            <div className="w-16 h-16 bg-gradient-to-br from-primary to-accent rounded-2xl flex items-center justify-center mx-auto mb-5 shadow-lg shadow-primary/20">
              <Icon name="ShieldCheck" size={32} color="white" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-1">Byte Pairs</h1>
            <p className="text-slate-500 text-sm">Admin Panel · Authorised Access Only</p>
          </div>

          <div className="bg-slate-900 border border-white/8 rounded-3xl p-8 shadow-2xl">
            <form onSubmit={handleLogin} className="space-y-5">
              <div className="space-y-1.5">
                <label className="text-xs font-bold uppercase tracking-widest text-slate-500">
                  Admin Password
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  className="w-full bg-slate-950 border border-white/10 rounded-xl px-4 py-3.5 text-white placeholder-slate-600 focus:border-primary/50 focus:outline-none transition-all"
                  placeholder="Enter your admin password"
                  required
                  autoFocus
                />
              </div>

              <AnimatePresence>
                {loginError && (
                  <motion.p
                    initial={{ opacity: 0, y: -6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    className="text-red-400 text-xs text-center bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2"
                  >
                    {loginError}
                  </motion.p>
                )}
              </AnimatePresence>

              <button
                type="submit"
                className="w-full bg-gradient-to-r from-primary to-accent py-3.5 rounded-xl font-bold text-white shadow-lg shadow-primary/20 hover:opacity-90 transition-all"
              >
                Access Dashboard
              </button>
            </form>
          </div>
          <p className="text-center text-slate-700 text-xs mt-6">
            This panel is not linked from any public page.
          </p>
        </motion.div>
      </div>
      </>
    );
  }

  // ── Filter rows ───────────────────────────────────────────────────────────────
  const rows = (data[activeTab] || []).filter(item => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    
    const clientName = (item.fullName || item.name || item.lead?.name || '').toLowerCase();
    const clientEmail = (item.email || item.lead?.email || '').toLowerCase();
    
    let contentStr = '';
    if (activeTab === 'submissions') {
      contentStr = `${item.requirements || ''} ${item.message || ''} ${item.projectType || ''} ${item.budget || ''} ${item.company || ''}`;
    } else if (activeTab === 'project_inquiries') {
      contentStr = `${item.selections?.projectType || ''} ${item.selections?.complexity || ''} ${item.selections?.timeline || ''} ${(item.selections?.features || []).join(' ')} ${item.estimate?.total || ''}`;
    } else {
      contentStr = `${item.projectBrief || ''} ${item.message || ''} ${item.description || ''}`;
    }
    
    return clientName.includes(q) || clientEmail.includes(q) || contentStr.toLowerCase().includes(q);
  });

  const activeTabMeta = TABS.find(t => t.id === activeTab);

  // ── Dashboard ─────────────────────────────────────────────────────────────────
  return (
    <>
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <div className="min-h-screen bg-slate-950 text-slate-200">

      {/* Top bar */}
      <div className="sticky top-0 z-50 bg-slate-950/90 backdrop-blur-xl border-b border-white/5 px-6 md:px-10 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-gradient-to-br from-primary to-accent rounded-xl flex items-center justify-center">
            <Icon name="LayoutDashboard" size={18} color="white" />
          </div>
          <div>
            <h1 className="text-base font-bold text-white leading-none">Byte Pairs Admin</h1>
            <p className="text-slate-600 text-[11px]">Client Enquiries Dashboard</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={fetchAll}
            title="Refresh data"
            className="p-2.5 bg-white/5 hover:bg-white/10 rounded-xl transition-all"
          >
            <Icon name="RefreshCw" size={16} className={loading ? 'animate-spin text-primary' : 'text-slate-400'} />
          </button>
          <button
            onClick={handleLogout}
            className="px-4 py-2 bg-red-500/10 text-red-400 border border-red-500/20 rounded-xl text-sm font-semibold hover:bg-red-500/20 transition-all"
          >
            Sign Out
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-10 py-10">

        {/* Stats */}
        {!tab && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-10">
            {TABS.map(tab => (
              <StatCard
                key={tab.id}
                label={tab.label}
                count={data[tab.id]?.length ?? 0}
                icon={tab.icon}
                color={tab.color}
              />
            ))}
          </div>
        )}

        {/* Tabs + Search */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div className="flex gap-1 bg-slate-900/60 border border-white/8 rounded-2xl p-1">
            {TABS.map(t => (
              <button
                key={t.id}
                onClick={() => { navigate('/admin'); setActiveTab(t.id); setSearchQuery(''); }}
                className={`relative flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all ${
                  activeTab === t.id && !tab
                    ? 'text-white'
                    : 'text-slate-500 hover:text-slate-300'
                }`}
              >
                {activeTab === t.id && !tab && (
                  <motion.div
                    layoutId="activeAdminTab"
                    className="absolute inset-0 bg-white/8 rounded-xl"
                  />
                )}
                <Icon name={t.icon} size={14} className="relative z-10" />
                <span className="relative z-10 hidden md:inline">{t.label}</span>
                <span className={`relative z-10 ml-1 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-${t.color}-500/20 text-${t.color}-400`}>
                  {data[t.id]?.length ?? 0}
                </span>
              </button>
            ))}

            <Link
              to="/admin/blog"
              className={`relative flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all ${
                tab
                  ? 'text-white'
                  : 'text-slate-500 hover:text-slate-300'
              }`}
            >
              {tab && (
                <motion.div
                  layoutId="activeAdminTab"
                  className="absolute inset-0 bg-white/8 rounded-xl"
                />
              )}
              <Icon name="BookOpen" size={14} className="relative z-10" />
              <span className="relative z-10 hidden md:inline">Blog Articles</span>
            </Link>
          </div>

          {!tab && (
            <div className="relative">
              <Icon name="Search" size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500" />
              <input
                type="text"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search by name, email, message…"
                className="bg-slate-900 border border-white/8 rounded-xl pl-9 pr-4 py-2.5 text-sm text-white placeholder-slate-600 focus:outline-none focus:border-primary/40 transition-all w-64"
              />
            </div>
          )}
        </div>

        {tab ? (
          <BlogAdminPanel activeSection={tab === 'blog' ? 'posts' : tab.replace('blog-', '')} editId={id} />
        ) : (
          <>
            {/* Table */}
            <div className="bg-slate-900/50 border border-white/8 rounded-3xl overflow-hidden">
              {loading ? (
                <div className="py-24 flex flex-col items-center gap-4 text-slate-600">
                  <motion.div animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 1, ease: 'linear' }}>
                    <Icon name="Loader2" size={36} />
                  </motion.div>
                  <p className="text-sm">Fetching enquiries…</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-sm">
                    <thead className="bg-white/[0.03] text-slate-500 text-xs font-bold uppercase tracking-widest border-b border-white/5">
                      <tr>
                        <th className="px-6 py-4">Client</th>
                        <th className="px-6 py-4">Message / Details</th>
                        <th className="px-6 py-4">Source</th>
                        <th className="px-6 py-4">Received</th>
                        <th className="px-6 py-4 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/[0.04]">
                      {rows.length === 0 ? (
                        <EmptyState tab={activeTab} />
                      ) : (
                        rows.map(item => {
                          const sourceInfo = SOURCE_LABELS[item.source] || { label: item.source || 'Unknown', color: 'bg-slate-700 text-slate-400' };
                          
                          // Resolve name, email, phone based on tab
                          let displayName = '—';
                          let displayEmail = '—';
                          let displayPhone = null;
                          
                          if (activeTab === 'project_inquiries') {
                            displayName = item.lead?.name || item.name || '—';
                            displayEmail = item.lead?.email || item.email || '—';
                            displayPhone = item.lead?.phone || item.phone || null;
                          } else {
                            displayName = item.fullName || item.name || '—';
                            displayEmail = item.email || '—';
                            displayPhone = item.phone || null;
                          }
                          
                          return (
                            <motion.tr
                              key={item.id}
                              layout
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              exit={{ opacity: 0 }}
                              className="hover:bg-white/[0.02] transition-colors group"
                            >
                              {/* Client */}
                              <td className="px-6 py-5">
                                <div className="flex items-center gap-3">
                                  <div className="w-9 h-9 rounded-full bg-gradient-to-br from-primary/30 to-accent/30 flex items-center justify-center text-xs font-bold text-white shrink-0">
                                    {(displayName !== '—' ? displayName : '?').charAt(0).toUpperCase()}
                                  </div>
                                  <div>
                                    <p className="text-white font-semibold">{displayName}</p>
                                    <p className="text-slate-500 text-xs">{displayEmail}</p>
                                    {displayPhone && (
                                      <p className="text-slate-600 text-[11px]">{displayPhone}</p>
                                    )}
                                  </div>
                                </div>
                              </td>

                              {/* Details */}
                              <td className="px-6 py-5 max-w-sm">
                                {activeTab === 'submissions' && (
                                  <div className="space-y-1.5">
                                    {(item.requirements || item.message || item.description) ? (
                                      <p className="text-slate-300 text-xs leading-relaxed italic line-clamp-3">
                                        "{item.requirements || item.message || item.description}"
                                      </p>
                                    ) : (
                                      <p className="text-slate-500 text-xs italic">No message</p>
                                    )}
                                    <div className="flex flex-wrap gap-1.5 pt-1">
                                      {item.company && (
                                        <span className="px-2 py-0.5 bg-slate-800 text-slate-300 text-[10px] font-medium rounded">
                                          Company: {item.company}
                                        </span>
                                      )}
                                      {item.projectType && (
                                        <span className="px-2 py-0.5 bg-primary/10 text-primary text-[10px] font-bold uppercase rounded">
                                          {item.projectType.replace('-', ' ')}
                                        </span>
                                      )}
                                      {item.budget && (
                                        <span className="px-2 py-0.5 bg-emerald-500/10 text-emerald-400 text-[10px] font-bold uppercase rounded">
                                          {item.budget}
                                        </span>
                                      )}
                                      {item.timeline && (
                                        <span className="px-2 py-0.5 bg-purple-500/10 text-purple-400 text-[10px] font-bold uppercase rounded">
                                          {item.timeline}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                )}

                                {activeTab === 'project_inquiries' && (
                                  <div className="space-y-2">
                                    <div className="flex flex-wrap gap-1.5">
                                      {item.selections?.projectType && (
                                        <span className="px-2 py-0.5 bg-primary/15 text-primary text-[10px] font-bold uppercase rounded">
                                          {item.selections.projectType}
                                        </span>
                                      )}
                                      {item.selections?.complexity && (
                                        <span className="px-2 py-0.5 bg-slate-800 text-slate-300 text-[10px] font-medium rounded">
                                          {item.selections.complexity}
                                        </span>
                                      )}
                                      {item.selections?.timeline && (
                                        <span className="px-2 py-0.5 bg-purple-500/10 text-purple-400 text-[10px] font-bold uppercase rounded">
                                          {item.selections.timeline}
                                        </span>
                                      )}
                                    </div>
                                    {item.selections?.features && item.selections.features.length > 0 && (
                                      <p className="text-[11px] text-slate-400">
                                        <span className="font-semibold text-slate-500">Features: </span>
                                        {item.selections.features.join(', ')}
                                      </p>
                                    )}
                                    {item.estimate && (
                                      <div className="flex items-center gap-2 pt-1">
                                        <span className="text-emerald-400 text-xs font-bold bg-emerald-500/10 px-2 py-0.5 rounded">
                                          Est: ₹{item.estimate.total?.toLocaleString() || '—'}
                                        </span>
                                        <span className="text-slate-500 text-[10px]">
                                          ({item.estimate.range?.min?.toLocaleString() || '—'} - {item.estimate.range?.max?.toLocaleString() || '—'})
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                )}

                                {activeTab === 'consultations' && (
                                  <div className="space-y-1.5">
                                    {(item.projectBrief || item.message || item.description) ? (
                                      <p className="text-slate-300 text-xs leading-relaxed italic line-clamp-3">
                                        "{item.projectBrief || item.message || item.description}"
                                      </p>
                                    ) : (
                                      <p className="text-slate-500 text-xs italic">No description</p>
                                    )}
                                  </div>
                                )}
                              </td>

                              {/* Source */}
                              <td className="px-6 py-5">
                                <span className={`px-2.5 py-1 rounded-lg text-[10px] font-bold uppercase tracking-wider ${sourceInfo.color}`}>
                                  {sourceInfo.label}
                                </span>
                              </td>

                              {/* Date */}
                              <td className="px-6 py-5 text-slate-500 text-xs whitespace-nowrap">
                                {formatDate(item.createdAt)}
                              </td>

                              {/* Actions */}
                              <td className="px-6 py-5 text-right">
                                <button
                                  onClick={() => handleDelete(item.id)}
                                  disabled={deleting === item.id}
                                  className="p-2 text-slate-600 hover:text-red-400 transition-colors rounded-lg hover:bg-red-500/10 disabled:opacity-40"
                                  title="Delete entry"
                                >
                                  {deleting === item.id
                                    ? <Icon name="Loader2" size={16} className="animate-spin" />
                                    : <Icon name="Trash2" size={16} />
                                  }
                                </button>
                              </td>
                            </motion.tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Footer */}
            <p className="text-center text-slate-700 text-xs mt-8">
              Byte Pairs Admin · Data stored in Supabase · {rows.length} {rows.length === 1 ? 'entry' : 'entries'} shown
            </p>
          </>
        )}
      </div>
    </div>
    </>
  );
};

export default AdminPage;
